import { supabase } from './supabase';
import { createLogger } from './logger';
import { Collaborator } from '../types/invitation';

const log = createLogger('collaboration');

/**
 * 協作者管理服務：處理邀請函協作者的 CRUD 操作
 */
export const collaboratorService = {
  /**
   * 取得指定邀請函的所有協作者
   * @param invitationId 邀請函 ID
   */
  async getCollaborators(invitationId: string): Promise<Collaborator[]> {
    try {
      // 使用分步驟查詢，避免複雜的 JOIN 問題
      const { data, error } = await supabase
        .from('invitation_collaborators')
        .select('id, user_id, created_at')
        .eq('invitation_id', invitationId);

      if (error) {
        log.error('取得協作者失敗:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      // 使用 RPC 函數批量取得用戶資訊
      const userIds = data.map(item => item.user_id);
      const { data: userInfoData, error: userInfoError } = await supabase
        .rpc('get_user_info_by_ids', { user_ids: userIds });

      if (userInfoError) {
        log.error('取得用戶資訊失敗:', userInfoError);
        // 備用方案：使用基本資訊
        return data.map(item => ({
          id: item.id,
          userId: item.user_id,
          displayName: `用戶 ${item.user_id.substring(0, 8)}`,
          email: `${item.user_id.substring(0, 8)}@...`,
          role: 'collaborator',
          permissionLevel: 'edit',
          sharedResources: ['guests', 'seating', 'timeline'],
          status: 'active',
          joinedAt: new Date(item.created_at),
          lastActiveAt: undefined
        }));
      }

      // 建立 user_id 到用戶資訊的映射
      const userInfoMap: {[key: string]: {email: string, displayName: string}} = {};
      userInfoData?.forEach((userInfo: any) => {
        userInfoMap[userInfo.user_id] = {
          email: userInfo.email,
          displayName: userInfo.display_name
        };
      });

      return data.map(item => {
        const userInfo = userInfoMap[item.user_id] || {
          email: `${item.user_id.substring(0, 8)}@...`,
          displayName: `用戶 ${item.user_id.substring(0, 8)}`
        };
        
        return {
          id: item.id,
          userId: item.user_id,
          displayName: userInfo.displayName,
          email: userInfo.email,
          role: 'collaborator',
          permissionLevel: 'edit',
          sharedResources: ['guests', 'seating', 'timeline'],
          status: 'active',
          joinedAt: new Date(item.created_at),
          lastActiveAt: undefined
        };
      });
    } catch (error) {
      log.error('取得協作者錯誤:', error);
      return [];
    }
  },

  /**
   * 透過 email 新增協作者
   * @param invitationId 邀請函 ID
   * @param email 協作者 email
   */
  async addCollaborator(invitationId: string, email: string): Promise<{ success: boolean; message: string }> {
    try {
      // 基本 email 格式驗證
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        return {
          success: false,
          message: '請輸入有效的 Email 格式'
        };
      }

      // 1. 使用 RPC 函數查詢用戶
      const { data: userData, error: userCheckError } = await supabase
        .rpc('find_user_by_email', { user_email: email.toLowerCase().trim() });

      if (userCheckError) {
        log.error('查詢用戶失敗:', userCheckError);
        return {
          success: false,
          message: '查詢用戶失敗，請稍後再試'
        };
      }

      // 檢查是否找到用戶
      if (!userData || userData.length === 0) {
        return {
          success: false,
          message: '找不到此 Email 的用戶，請確認 Email 是否正確或請對方先註冊帳號'
        };
      }

      const targetUser = userData[0];

      // 2. 檢查是否已經是協作者
      const { data: existingCollaborator } = await supabase
        .from('invitation_collaborators')
        .select('id')
        .eq('invitation_id', invitationId)
        .eq('user_id', targetUser.user_id)
        .single();

      if (existingCollaborator) {
        return {
          success: false,
          message: `${targetUser.display_name} 已經是協作者了`
        };
      }

      // 3. 新增協作者
      const { error: insertError } = await supabase
        .from('invitation_collaborators')
        .insert({
          invitation_id: invitationId,
          user_id: targetUser.user_id
        });

      if (insertError) {
        log.error('新增協作者失敗:', insertError);
        return {
          success: false,
          message: '新增協作者失敗，請稍後再試'
        };
      }

      return {
        success: true,
        message: `成功邀請 ${targetUser.display_name} 成為協作者`
      };
    } catch (error) {
      log.error('新增協作者錯誤:', error);
      return {
        success: false,
        message: '發生未知錯誤，請稍後再試'
      };
    }
  },

  /**
   * 移除協作者
   * @param collaboratorId 協作者記錄 ID
   */
  async removeCollaborator(collaboratorId: string): Promise<{ success: boolean; message: string }> {
    try {
      const { error } = await supabase
        .from('invitation_collaborators')
        .delete()
        .eq('id', collaboratorId);

      if (error) {
        log.error('移除協作者失敗:', error);
        return {
          success: false,
          message: '移除協作者失敗，請稍後再試'
        };
      }

      return {
        success: true,
        message: '成功移除協作者'
      };
    } catch (error) {
      log.error('移除協作者錯誤:', error);
      return {
        success: false,
        message: '發生未知錯誤，請稍後再試'
      };
    }
  },

  /**
   * 檢查當前用戶是否為指定邀請函的協作者
   * @param userId 用戶 ID
   * @param invitationId 邀請函 ID
   */
  async isCollaborator(userId: string, invitationId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('invitation_collaborators')
        .select('id')
        .eq('user_id', userId)
        .eq('invitation_id', invitationId)
        .single();

      return !error && !!data;
    } catch (error) {
      log.error('檢查協作者狀態錯誤:', error);
      return false;
    }
  }
};